import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * A button that asks twice. The first click arms it; the second confirms.
 * A double-click does not confirm. Blur, or the arm timeout, puts it back.
 *
 * This is the confirm control for destructive actions in the admin.
 */
export default function TwoClickConfirmButton({
    onConfirm,
    children,
    confirmChildren,
    className = '',
    confirmClassName,
    title,
    confirmTitle,
    ariaLabel,
    confirmAriaLabel,
    disabled = false,
    armTimeoutMs = 0,
    preventDoubleClickMs = 400,
    resetOnBlur = true,
    blurResetDelayMs = 200,
    onBeforeToggle,
    tabIndex,
    armed: armedControlled,
    onArmedChange,
}) {
    const isControlled = armedControlled !== undefined;
    const [armedUncontrolled, setArmedUncontrolled] = useState(false);
    const armed = isControlled ? Boolean(armedControlled) : armedUncontrolled;
    const armedAtRef = useRef(0);
    const timerRef = useRef(null);

    const setArmed = (next) => {
        if (!isControlled) setArmedUncontrolled(next);
        onArmedChange?.(next);
    };

    const clearArmTimer = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    const disarm = () => {
        clearArmTimer();
        setArmed(false);
    };

    const arm = () => {
        setArmed(true);
        armedAtRef.current = Date.now();
        clearArmTimer();
        if (armTimeoutMs > 0) {
            timerRef.current = setTimeout(() => {
                setArmed(false);
                timerRef.current = null;
            }, armTimeoutMs);
        }
    };

    useEffect(() => {
        if (disabled) disarm();
        return () => clearArmTimer();
        // disarm when the action is no longer available
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disabled]);

    useEffect(() => {
        if (!isControlled) return undefined;
        if (!armedControlled) {
            clearArmTimer();
            return undefined;
        }
        armedAtRef.current = Date.now();
        clearArmTimer();
        if (armTimeoutMs > 0) {
            timerRef.current = setTimeout(() => {
                setArmed(false);
                timerRef.current = null;
            }, armTimeoutMs);
        }
        return () => clearArmTimer();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isControlled, armedControlled, armTimeoutMs]);

    const handleClick = (event) => {
        onBeforeToggle?.(event);
        if (disabled) return;
        if (!armed) {
            arm();
            return;
        }
        if (Date.now() - armedAtRef.current < preventDoubleClickMs) return;
        disarm();
        onConfirm();
    };

    const fallbackConfirm = (
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path strokeLinecap="round" d="M9.5 9a2.5 2.5 0 115 0c0 1.5-2.5 2-2.5 3.5" />
            <circle cx="12" cy="17" r="0.8" fill="currentColor" stroke="none" />
        </svg>
    );

    return (
        <button
            type="button"
            disabled={disabled}
            tabIndex={tabIndex}
            onClick={handleClick}
            onBlur={resetOnBlur ? () => { setTimeout(() => setArmed(false), blurResetDelayMs); } : undefined}
            title={armed ? (confirmTitle ?? title) : title}
            aria-label={armed ? (confirmAriaLabel ?? ariaLabel) : ariaLabel}
            className={armed ? (confirmClassName ?? className) : className}
        >
            {armed ? (confirmChildren ?? fallbackConfirm) : children}
        </button>
    );
}

TwoClickConfirmButton.propTypes = {
    onConfirm: PropTypes.func.isRequired,
    children: PropTypes.node,
    confirmChildren: PropTypes.node,
    className: PropTypes.string,
    confirmClassName: PropTypes.string,
    title: PropTypes.string,
    confirmTitle: PropTypes.string,
    ariaLabel: PropTypes.string,
    confirmAriaLabel: PropTypes.string,
    disabled: PropTypes.bool,
    armTimeoutMs: PropTypes.number,
    preventDoubleClickMs: PropTypes.number,
    resetOnBlur: PropTypes.bool,
    blurResetDelayMs: PropTypes.number,
    onBeforeToggle: PropTypes.func,
    tabIndex: PropTypes.number,
    armed: PropTypes.bool,
    onArmedChange: PropTypes.func,
};
